/**
 * Stunt 5 — Honesty Index badge.
 *
 * Public per-agent score lookup. Renders nothing while loading and nothing
 * when the API returns 404 (flag OFF), so it is safe to mount on every
 * property detail page.
 *
 * Props:
 *   userId   — string. Required. The seller/agent user_id.
 *   compact? — boolean. If true, renders a smaller inline pill.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldCheck, AlertTriangle, BadgeCheck, Info } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Module-level cache so multiple PropertyCards on /buy don't each fire their
// own /api/honesty/agent/{id} request. Keyed by userId; value is either the
// resolved data object or the in-flight Promise.
const _honestyCache = new Map();

const fetchHonesty = (userId) => {
  if (_honestyCache.has(userId)) return _honestyCache.get(userId);
  const p = axios
    .get(`${BACKEND_URL}/api/honesty/agent/${userId}`)
    .then((r) => {
      _honestyCache.set(userId, r.data);
      return r.data;
    })
    .catch((err) => {
      _honestyCache.set(userId, { __err: true, status: err.response?.status });
      return _honestyCache.get(userId);
    });
  _honestyCache.set(userId, p);
  return p;
};

const TIER_STYLES = {
  excellent: {
    bg: 'bg-emerald-50',
    border: 'border-emerald-300',
    text: 'text-emerald-800',
    icon: BadgeCheck,
    label: 'Excellent honesty',
  },
  good: {
    bg: 'bg-sky-50',
    border: 'border-sky-300',
    text: 'text-sky-800',
    icon: ShieldCheck,
    label: 'Good honesty',
  },
  fair: {
    bg: 'bg-amber-50',
    border: 'border-amber-300',
    text: 'text-amber-900',
    icon: ShieldCheck,
    label: 'Fair honesty',
  },
  caution: {
    bg: 'bg-red-50',
    border: 'border-red-300',
    text: 'text-red-800',
    icon: AlertTriangle,
    label: 'Use caution',
  },
  insufficient: {
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    text: 'text-gray-600',
    icon: Info,
    label: 'New agent',
  },
};

const HonestyBadge = ({ userId, compact = false }) => {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let alive = true;
    Promise.resolve(fetchHonesty(userId)).then((res) => {
      if (!alive) return;
      if (!res || res.__err) {
        setHidden(true);
      } else {
        setData(res);
      }
    });
    return () => {
      alive = false;
    };
  }, [userId]);

  if (hidden || !data) return null;

  const tier = data.tier || 'insufficient';
  const style = TIER_STYLES[tier] || TIER_STYLES.insufficient;
  const Icon = style.icon;

  if (compact) {
    return (
      <span
        data-testid="honesty-badge-compact"
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${style.bg} ${style.border} ${style.text}`}
        title={style.label}
      >
        <Icon className="h-3 w-3" />
        {tier === 'insufficient' ? 'New agent' : `${data.score}/100`}
      </span>
    );
  }

  return (
    <div
      data-testid="honesty-badge"
      className={`rounded-xl border-2 p-4 ${style.bg} ${style.border}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Icon className={`h-5 w-5 ${style.text}`} />
          <div>
            <div className={`text-[10px] uppercase tracking-widest font-semibold ${style.text}`}>
              Honesty Index
            </div>
            <div className={`text-base font-bold ${style.text}`}>{style.label}</div>
          </div>
        </div>
        {tier !== 'insufficient' && (
          <div className={`text-2xl font-extrabold ${style.text}`} data-testid="honesty-score">
            {data.score}
            <span className="text-xs font-medium opacity-60">/100</span>
          </div>
        )}
      </div>

      {tier === 'insufficient' ? (
        <p className="text-xs text-gray-600 mt-2">
          We need at least {data.min_required || 3} listings to compute a
          reliable score. This agent has {data.total_listings || 0} so far.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-3 mt-3 text-[11px]">
          <div>
            <div className="text-gray-500 uppercase tracking-wider text-[10px]">
              Price drops
            </div>
            <div className="font-semibold text-gray-800">
              {Math.round((data.drop_rate || 0) * 100)}% of listings
              {data.avg_drop_pct ? (
                <span className="font-normal text-gray-500">
                  {' '}
                  · avg −{data.avg_drop_pct}%
                </span>
              ) : null}
            </div>
          </div>
          <div>
            <div className="text-gray-500 uppercase tracking-wider text-[10px]">
              Asking vs area median
            </div>
            <div className="font-semibold text-gray-800">
              {data.median_gap_pct > 0 ? '+' : ''}
              {data.median_gap_pct}%
            </div>
          </div>
          <div>
            <div className="text-gray-500 uppercase tracking-wider text-[10px]">
              Avg days on market
            </div>
            <div className="font-semibold text-gray-800">
              {Math.round(data.avg_dom_days || 0)} days
            </div>
          </div>
          <div>
            <div className="text-gray-500 uppercase tracking-wider text-[10px]">
              Total listings
            </div>
            <div className="font-semibold text-gray-800">{data.total_listings}</div>
          </div>
        </div>
      )}

      <div className="text-[10px] text-gray-400 mt-3 leading-relaxed">
        Computed from observable listing behaviour — never the agent's word.
        Higher = closer to area medians, fewer price corrections, faster sales.
      </div>
    </div>
  );
};

export default HonestyBadge;
